import {
	CopyControl,
	CopyControlHandle,
	DateString,
	FormattedText,
	Space
} from "client/Components";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useRef
} from "react";
import { LinkAudienceKind, LinkPermission } from "./common";
import { LinkIcon } from "./LinkIcon";
import styles from "./SharedLink.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

export type SharedLinkHandle = {
	copy: () => void;
};

export type SharedLinkProps = {
	audience: LinkAudienceKind;
	permission: LinkPermission;
	url: string;
	expirationDate?: string | Date;
	trackingId: string;
	timezone: TimeZone;
	buttonKind: "primary" | "secondary";
};

export const SharedLink = forwardRef(
	(props: SharedLinkProps, ref: Ref<SharedLinkHandle>): ReactElement => {
		const {
			audience,
			permission,
			url,
			expirationDate,
			trackingId,
			timezone,
			buttonKind
		} = props;

		const copyRef: RefObject<CopyControlHandle | null> = useRef<CopyControlHandle>(null);

		useImperativeHandle(
			ref,
			(): SharedLinkHandle => ({
				copy: () => copyRef.current
					?.invoke()
			}),
			[]
		);

		return (
			<div className={styles.root}>
				<LinkIcon kind={audience} />

				<div className={styles.content}>
					<CopyControl
						value={url}
						trackingId={generateTrackingId(trackingId, "__copy")}
						ref={copyRef}
						buttonKind={buttonKind}
					/>

					<p className={styles.description}>
						{localize(linkDescriptionToLocalizationKeyMap[audience][permission])}

						{isNotNil(expirationDate) && (
							<>
								<Space />
								(
								<FormattedText
									text={localize("_ShareLinkExpirationHint")}
									thingsToReplace={[
										{
											what: "{0}",
											filler: (
												<DateString
													value={expirationDate}
													format="DMY"
													timezone={timezone}
												/>
											)
										}
									]}
								/>
								)
							</>
						)}
					</p>
				</div>
			</div>
		);
	}
);
SharedLink.displayName = "SharedLink";

export const linkDescriptionToLocalizationKeyMap: Record<LinkAudienceKind, Record<LinkPermission, LocalizationKey>> = {
	[LinkAudienceKind.Anonymous]: {
		[LinkPermission.CanEdit]: "_AnyoneWithTheLinkCanEdit",
		[LinkPermission.CanView]: "_AnyoneWithTheLinkCanView"
	},
	[LinkAudienceKind.Organization]: {
		[LinkPermission.CanEdit]: "_PeopleInThisOrganisationWithTheLinkCanEdit",
		[LinkPermission.CanView]: "_PeopleInThisOrganisationWithTheLinkCanView"
	},
	[LinkAudienceKind.SpecificPeople]: {
		[LinkPermission.CanEdit]: "_PeopleYouSpecifyCanEdit",
		[LinkPermission.CanView]: "_PeopleYouSpecifyCanView"
	},
	[LinkAudienceKind.PeopleWithExistingAccess]: {
		[LinkPermission.CanEdit]: "_OnlyPeopleWithExistingAccess",
		[LinkPermission.CanView]: "_OnlyPeopleWithExistingAccess"
	}
};
